import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";
import type { Database } from "@/integrations/supabase/types";

export type EncounterSpecialty = Database["public"]["Enums"]["encounter_speciality_type"];

export interface EncounterBreakdownMetric {
  total: number;
  bySpecialty: Record<EncounterSpecialty, number>;
}

export interface SentBackStatusCounts {
  pending: number;
  success: number;
  error: number;
  rejected: number;
  skipped: number;
}

export interface SentBackBreakdown {
  totals: SentBackStatusCounts;
  bySpecialty: Record<EncounterSpecialty, SentBackStatusCounts>;
  recodes: {
    totals: SentBackStatusCounts;
    bySpecialty: Record<EncounterSpecialty, SentBackStatusCounts>;
  };
}

export interface EncounterBreakdown {
  valid: EncounterBreakdownMetric;
  classifiable: EncounterBreakdownMetric;
  excludedClassifiable: EncounterBreakdownMetric;
  successfullyCoded: EncounterBreakdownMetric;
  failedToClassify: EncounterBreakdownMetric;
  successfullyClassified: EncounterBreakdownMetric;
  notCoded: EncounterBreakdownMetric;
  /**
   * Exclusion breakdown by specialty (all specialties present).
   * endoscopy: is_follow_up, unsupported_procedure, awaiting_histology, not_daycase.
   * well_baby: was_excluded.
   * chemo: is_clinical_trial.
   * haematology: is_clinical_trial.
   * ophthalmology: excluded_count (no per-reason breakdown).
   */
  excludedBreakdownBySpecialty?: Partial<Record<EncounterSpecialty, Record<string, number>>>;
  sentBack: SentBackBreakdown;
}

const ENCOUNTERS_BREAKDOWN_URL = `${QA_API_DOMAIN}/encounters/breakdown`;
const GAP_BREAKDOWN_URL = `${QA_API_DOMAIN}/encounters/gap-breakdown`;

export interface GetEncounterBreakdownOptions {
  customerIds?: string[];
  dischargeDateFrom?: string;
  dischargeDateTo?: string;
}

export async function getEncounterBreakdown(options?: GetEncounterBreakdownOptions): Promise<EncounterBreakdown> {
  const params = new URLSearchParams();
  if (options?.customerIds?.length) {
    params.set("customer_ids", options.customerIds.join(","));
  }
  if (options?.dischargeDateFrom) {
    params.set("discharge_date_from", options.dischargeDateFrom);
  }
  if (options?.dischargeDateTo) {
    params.set("discharge_date_to", options.dischargeDateTo);
  }

  const url = `${ENCOUNTERS_BREAKDOWN_URL}${params.toString() ? `?${params}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      issues?: unknown;
    };
    const message = body.message ?? `Failed to fetch encounter breakdown (${res.status})`;
    throw new Error(message);
  }

  return res.json() as Promise<EncounterBreakdown>;
}

export type GapDocState =
  | "present"
  | "missing_feed"
  | "awaiting_scrape_not_triggerable"
  | "awaiting_scrape_triggerable";

export interface GapDocBreakdown {
  key: string;
  displayKey: string;
  fileIdentifiers: string[];
  isOneOfGroup: boolean;
  source: "feed" | "scrape";
  counts: Record<GapDocState, number>;
}

export interface GapSpecialtyBreakdown {
  providerId: string;
  specialty: EncounterSpecialty;
  received: number;
  docs: GapDocBreakdown[];
}

export interface GapBreakdown {
  bySpecialty: GapSpecialtyBreakdown[];
}

export interface GapEncounter {
  encounterId: string;
  mrn: string | null;
  dischargeDate: string | null;
}

export interface GapEncountersResult {
  encounters: GapEncounter[];
}

export interface GapTrendPoint {
  date: string;
  received: number;
  classifiable: number;
}

export interface GapTrendBranch {
  providerId: string;
  specialty: EncounterSpecialty;
  points: GapTrendPoint[];
}

export interface GapTrend {
  byBranch: GapTrendBranch[];
}

export interface GapQuery {
  customerIds?: string[];
  dischargeDateFrom?: string;
  dischargeDateTo?: string;
}

function gapParams(query?: GapQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query?.customerIds?.length) params.set("customer_ids", query.customerIds.join(","));
  if (query?.dischargeDateFrom) params.set("discharge_date_from", query.dischargeDateFrom);
  if (query?.dischargeDateTo) params.set("discharge_date_to", query.dischargeDateTo);
  return params;
}

async function throwOnError(res: Response, label: string): Promise<void> {
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { message?: string; issues?: unknown };
  throw new Error(body.message ?? `${label} (${res.status})`);
}

export async function getGapBreakdown(query?: GapQuery): Promise<GapBreakdown> {
  const params = gapParams(query);
  const qs = params.toString();
  const url = `${GAP_BREAKDOWN_URL}${qs ? `?${qs}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, { method: "GET", headers: { "Content-Type": "application/json" } });

  await throwOnError(res, "Failed to fetch gap breakdown");

  return res.json() as Promise<GapBreakdown>;
}

export async function getGapTrend(query?: GapQuery): Promise<GapTrend> {
  const params = gapParams(query);
  const qs = params.toString();
  const url = `${GAP_BREAKDOWN_URL}/trend${qs ? `?${qs}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, { method: "GET", headers: { "Content-Type": "application/json" } });

  await throwOnError(res, "Failed to fetch gap trend");

  return res.json() as Promise<GapTrend>;
}

export async function getGapEncounters(
  query: GapQuery & { providerId: string; specialty: EncounterSpecialty; docKey: string; state: GapDocState },
): Promise<GapEncountersResult> {
  const params = gapParams(query);
  params.set("provider_id", query.providerId);
  params.set("specialty", query.specialty);
  params.set("doc_key", query.docKey);
  params.set("state", query.state);
  const url = `${GAP_BREAKDOWN_URL}/encounters?${params}`;
  const res = await fetchWithSupabaseAuth(url, { method: "GET", headers: { "Content-Type": "application/json" } });

  await throwOnError(res, "Failed to fetch gap encounters");

  return res.json() as Promise<GapEncountersResult>;
}
